import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiSearch,
  FiFilter,
  FiTrash2,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiAlertTriangle,
  FiUsers,
} from "react-icons/fi";
import {
  useGetAdminUsersQuery,
  useDeleteAdminUserMutation,
  useRestoreAdminUserMutation,
  useSyncAllUsersWalletActivitiesMutation,
} from "../../redux/slices/api/adminApiSlice";
import { Bars } from "react-loader-spinner";
import { toast } from "react-toastify";
import WalletDisplay from "../../components/common/WalletDisplay";

const AdminUsers = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    role: "",
    auth_provider: "",
    deleted: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [permanentDelete, setPermanentDelete] = useState(false);

  const { data, isLoading, error, refetch } = useGetAdminUsersQuery({
    page,
    limit: 20,
    search,
    ...filters,
  });

  const [deleteUser, { isLoading: isDeleting }] = useDeleteAdminUserMutation();
  const [restoreUser, { isLoading: isRestoring }] = useRestoreAdminUserMutation();
  const [syncAllWalletActivities, { isLoading: isSyncingAll }] = useSyncAllUsersWalletActivitiesMutation();

  const handleSyncAllWalletActivities = async () => {
    try {
      const result = await syncAllWalletActivities().unwrap();
      if (result.synced) {
        const { usersProcessed, usersWithUpdates, communityMemberships, fundContributions } = result.summary;
        toast.success(
          `Synced ${usersProcessed} users: ${communityMemberships.updated} memberships, ${fundContributions.updated} contributions updated`
        );
      }
    } catch (err) {
      toast.error(err?.data?.message || "Failed to sync wallet activities");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ role: "", auth_provider: "", deleted: "" });
    setSearch("");
    setSearchInput("");
    setPage(1);
  };

  const handleDeleteClick = (user, permanent = false) => {
    setSelectedUser(user);
    setPermanentDelete(permanent);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      await deleteUser({
        id: selectedUser._id,
        permanent: permanentDelete,
      }).unwrap();
      toast.success(
        permanentDelete
          ? "User permanently deleted"
          : "User deleted successfully"
      );
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete user");
    }
  };

  const handleRestore = async (userId) => {
    try {
      await restoreUser(userId).unwrap();
      toast.success("User restored successfully");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to restore user");
    }
  };

  const getDisplayName = (user) => {
    if (user.role === "organization") {
      return user.fundername || "N/A";
    }
    return `${user.firstname || ""} ${user.lastname || ""}`.trim() || "N/A";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Failed to load users. Please try again.</p>
        <button
          onClick={refetch}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Users</h1>
          <p className="text-gray-500">
            Manage all platform users ({data?.pagination?.total || 0} total)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSyncAllWalletActivities}
            disabled={isSyncingAll}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            title="Link historical wallet activities (memberships, contributions) to user profiles"
          >
            {isSyncingAll ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <FiUsers size={16} />
            )}
            {isSyncingAll ? "Syncing..." : "Sync All Wallets"}
          </button>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-light-primary transition-colors"
          >
            <FiRefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name, email, or wallet address..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
          </form>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors ${
              showFilters || Object.values(filters).some(Boolean)
                ? "bg-primary text-white border-primary"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            <FiFilter size={16} />
            Filters
            {Object.values(filters).filter(Boolean).length > 0 && (
              <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                {Object.values(filters).filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange("role", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">All Roles</option>
                <option value="applicant">Applicant</option>
                <option value="organization">Organization</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auth Provider
              </label>
              <select
                value={filters.auth_provider}
                onChange={(e) =>
                  handleFilterChange("auth_provider", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">All Providers</option>
                <option value="wallet">Wallet</option>
                <option value="google">Google</option>
                <option value="github">GitHub</option>
                <option value="orcid">ORCID</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.deleted}
                onChange={(e) => handleFilterChange("deleted", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">Active Only</option>
                <option value="true">Deleted Only</option>
                <option value="all">All Users</option>
              </select>
            </div>

            <button
              onClick={clearFilters}
              className="text-sm text-primary hover:text-light-primary flex items-center gap-1"
            >
              <FiX size={14} />
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Bars height="40" width="40" color="#8538FF" ariaLabel="loading" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      User
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Email
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Role
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Auth
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Joined
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">
                      Status
                    </th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.users?.map((user) => (
                    <tr
                      key={user._id}
                      className={`hover:bg-gray-50 ${
                        user.deleted ? "bg-red-50/50" : ""
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {user.images?.profile ? (
                              <img
                                src={user.images.profile}
                                alt={getDisplayName(user)}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-primary font-semibold">
                                {getDisplayName(user)[0]?.toUpperCase() || "?"}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-secondary truncate max-w-[200px]">
                              {getDisplayName(user)}
                            </p>
                            {user.public_address && (
                              <p className="text-xs text-gray-400 truncate max-w-[200px]">
                                <WalletDisplay address={user.public_address} />
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-600 truncate max-w-[200px]">
                          {user.email || "—"}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.role === "organization"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600 capitalize text-sm">
                          {user.auth_provider || "wallet"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600 text-sm">
                          {formatDate(user.createdAt)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.deleted
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {user.deleted ? "Deleted" : "Active"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/users/${user._id}`}
                            className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <FiEye size={16} />
                          </Link>
                          {user.deleted ? (
                            <>
                              <button
                                onClick={() => handleRestore(user._id)}
                                disabled={isRestoring}
                                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                title="Restore User"
                              >
                                <FiRefreshCw size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(user, true)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Permanently Delete"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleDeleteClick(user)}
                                className="p-2 text-orange-500 hover:bg-orange-100 rounded-lg transition-colors"
                                title="Soft Delete (can restore later)"
                              >
                                <FiTrash2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(user, true)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Permanently Delete (cannot restore)"
                              >
                                <FiAlertTriangle size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {data?.users?.length === 0 && (
              <div className="text-center py-12">
                <FiUsers className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No users found</p>
              </div>
            )}

            {/* Pagination */}
            {data?.pagination && data.pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {(data.pagination.page - 1) * data.pagination.limit + 1}{" "}
                  to{" "}
                  {Math.min(
                    data.pagination.page * data.pagination.limit,
                    data.pagination.total
                  )}{" "}
                  of {data.pagination.total} users
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {data.pagination.pages}
                  </span>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(data.pagination.pages, p + 1))
                    }
                    disabled={page === data.pagination.pages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <FiAlertTriangle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary">
                  {permanentDelete ? "Permanently Delete User?" : "Delete User?"}
                </h3>
                <p className="text-sm text-gray-500">
                  {getDisplayName(selectedUser)}
                </p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              {permanentDelete
                ? "This action cannot be undone. The user and all their data will be permanently removed from the database."
                : "The user will be soft-deleted and can be restored later. Their data will remain in the database."}
            </p>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {permanentDelete ? "Delete Permanently" : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
